// ON-407: product analytics for the web (PostHog).
//
// Uses the SAME PostHog project as the mobile app (stazon-app
// src/services/analytics.ts) so a visitor identified on the web with their
// Supabase user id stitches to the same person as in the app. The app tells
// environments apart with the `app_environment` super property
// ('development' | 'uat' | 'production', ON-375) — the web sends the same values.
//
// Missing VITE_POSTHOG_KEY → every call is a no-op (one console.warn). Nothing
// here may throw into the UI.
//
// Do NOT emit event_rsvp_created from the web: register_for_event emits it
// server-side with rsvp_source (ON-423).

import { trackingParams } from './rsvpLink'

export type AppEnvironment = 'development' | 'uat' | 'production'

export type WebEventName =
  | 'event_page_opened'
  | 'rsvp_attempted'
  | 'auth_completed'
  | 'auth_link_sent'
  | 'auth_link_opened'
  | 'registration_completed'

type Props = Record<string, unknown>

/** The slice of posthog-js this module uses — swappable in tests. */
export interface AnalyticsClient {
  init: (key: string, config: Record<string, unknown>) => unknown
  register: (props: Props) => unknown
  capture: (name: string, props?: Props) => unknown
  identify: (id: string) => unknown
  reset: () => unknown
}

export interface AnalyticsEnv {
  VITE_POSTHOG_KEY?: string
  VITE_POSTHOG_HOST?: string
  VITE_APP_ENV?: string
  VITE_SITE_URL?: string
}

const ENVS: readonly AppEnvironment[] = ['development', 'uat', 'production']
const PROD_HOSTS = ['app.stazon.cc']
const DEFAULT_HOST = 'https://us.i.posthog.com'

/**
 * VITE_APP_ENV when it is one of the app's values, else derived from
 * VITE_SITE_URL. Anything unrecognised is 'development', so a misconfigured
 * build can never masquerade as production (same rule as the app).
 */
export function resolveAppEnvironment(explicit: string | undefined, siteUrl: string | undefined): AppEnvironment {
  if (explicit && (ENVS as readonly string[]).includes(explicit)) return explicit as AppEnvironment
  if (!siteUrl) return 'development'
  let host: string
  try {
    host = new URL(siteUrl).hostname
  } catch {
    return 'development'
  }
  if (PROD_HOSTS.includes(host)) return 'production'
  if (host.split('.').includes('uat')) return 'uat'
  return 'development'
}

function clean(props: Props): Props {
  return Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined))
}

/** event_id, club_id and the share link's ref / src, plus `extra`. */
export function buildEventProperties(
  ctx: { eventId: string; clubId?: string | null; search: string },
  extra: Props = {},
): Props {
  return clean({
    event_id: ctx.eventId,
    club_id: ctx.clubId ?? undefined,
    ...trackingParams(ctx.search),
    ...extra,
  })
}

// Auth links carry secrets (token_hash, ?code=, #access_token=). PostHog
// attaches the page URL to every event, so they are stripped before sending.
const SECRET_PARAMS = ['token_hash', 'token', 'code', 'access_token', 'refresh_token', 'provider_token']

export function scrubUrl(value: string): string {
  if (!/^https?:\/\//i.test(value)) return value
  try {
    const url = new URL(value)
    for (const key of SECRET_PARAMS) url.searchParams.delete(key)
    url.hash = ''
    return url.toString().replace(/\?$/, '')
  } catch {
    return value
  }
}

function scrubBag(bag: Props | undefined): Props | undefined {
  if (!bag) return bag
  const out: Props = { ...bag }
  for (const [key, v] of Object.entries(out)) {
    if (typeof v === 'string' && /url|referrer/i.test(key)) out[key] = scrubUrl(v)
  }
  return out
}

interface ScrubbableEvent {
  properties: Props
  $set?: Props
  $set_once?: Props
  [k: string]: unknown
}

export function scrubEvent<T extends ScrubbableEvent>(event: T | null): T | null {
  if (!event) return event
  return {
    ...event,
    properties: scrubBag(event.properties) ?? {},
    ...(event.$set ? { $set: scrubBag(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubBag(event.$set_once) } : {}),
  }
}

let client: AnalyticsClient | null = null
let environment: AppEnvironment = 'development'
let warned = false
let identifiedAs: string | null = null
// Calls made while posthog-js is still loading; replayed in order once it is.
let queue: Array<(c: AnalyticsClient) => void> | null = null

function safely(fn: () => unknown) {
  try {
    fn()
  } catch {
    // Analytics must never break the page.
  }
}

function withClient(fn: (c: AnalyticsClient) => void) {
  if (client) safely(() => fn(client as AnalyticsClient))
  else if (queue) queue.push(fn)
}

// posthog-js is loaded on demand so it stays out of the main bundle.
const loadPosthog = () => import('posthog-js').then((m) => m.default as unknown as AnalyticsClient)

/**
 * Starts analytics. `impl` is the client (tests) or a loader for it (default:
 * posthog-js, imported lazily); calls made before it loads are queued.
 */
export function initAnalytics(
  env: AnalyticsEnv = import.meta.env as AnalyticsEnv,
  impl: AnalyticsClient | (() => Promise<AnalyticsClient>) = loadPosthog,
): void {
  if (client || queue) return
  const key = env.VITE_POSTHOG_KEY
  if (!key) {
    if (!warned) {
      warned = true
      console.warn('[analytics] VITE_POSTHOG_KEY is not set — analytics disabled')
    }
    return
  }
  environment = resolveAppEnvironment(env.VITE_APP_ENV, env.VITE_SITE_URL)

  const start = (c: AnalyticsClient) => {
    c.init(key, {
      api_host: env.VITE_POSTHOG_HOST || DEFAULT_HOST,
      person_profiles: 'identified_only',
      // Explicit events only: no click autocapture and no session replay on
      // pages with password fields.
      autocapture: false,
      disable_session_recording: true,
      before_send: scrubEvent,
    })
    c.register({ app_environment: environment, platform: 'web' })
    client = c
  }

  if (typeof impl !== 'function') {
    safely(() => start(impl))
    return
  }
  queue = []
  impl()
    .then((c) => {
      const pending = queue ?? []
      queue = null
      safely(() => start(c))
      for (const fn of pending) withClient(fn)
    })
    .catch(() => {
      queue = null
      console.warn('[analytics] PostHog failed to load — analytics disabled')
    })
}

export function track(name: WebEventName, props: Props = {}): void {
  const payload = clean({ app_environment: environment, ...props })
  withClient((c) => c.capture(name, payload))
}

type OnceStore = Pick<Storage, 'getItem' | 'setItem'>

function local(): OnceStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/**
 * track(), at most once per `key` on this browser (persisted in localStorage),
 * e.g. registration_completed per user. Returns whether it fired. Without
 * storage it still fires — a rare duplicate beats losing the event.
 */
export function trackOnce(key: string, name: WebEventName, props: Props = {}, store: OnceStore | null = local()): boolean {
  const storageKey = `stazon:once:${key}`
  try {
    if (store?.getItem(storageKey)) return false
    store?.setItem(storageKey, '1')
  } catch {
    /* storage blocked */
  }
  track(name, props)
  return true
}

/** posthog.identify with the Supabase user id — on sign-in and on session restore. */
export function identifyUser(userId: string): void {
  if ((!client && !queue) || identifiedAs === userId) return
  identifiedAs = userId
  withClient((c) => c.identify(userId))
}

/** posthog.reset on sign-out, so the next visitor on this browser is not merged in. */
export function resetUser(): void {
  if ((!client && !queue) || !identifiedAs) return
  identifiedAs = null
  withClient((c) => c.reset())
}

export function __resetAnalyticsForTests(): void {
  client = null
  environment = 'development'
  warned = false
  identifiedAs = null
  queue = null
}
